// Elements the editor builds a block from, left exactly as written. Media are here because
// the editor imports them as blocks, and a block inside the paragraph this adds would be
// invalid.
const EDITOR_BLOCK_TAGS = new Set([
	'AUDIO',
	'BLOCKQUOTE',
	'EMBED',
	'H1',
	'H2',
	'H3',
	'H4',
	'H5',
	'H6',
	'HR',
	'IFRAME',
	'IMG',
	'LI',
	'OL',
	'P',
	'PRE',
	'UL',
	'VIDEO',
]);

// Block-level html the editor has no block for. It reads their children as if they stood
// where the element does, so inline content inside one is loose at the editor's root.
const CONTAINER_TAGS = new Set([
	'ADDRESS',
	'ARTICLE',
	'ASIDE',
	'CAPTION',
	'CENTER',
	'DD',
	'DETAILS',
	'DIV',
	'DL',
	'DT',
	'FIELDSET',
	'FIGCAPTION',
	'FIGURE',
	'FOOTER',
	'FORM',
	'HEADER',
	'HGROUP',
	'LEGEND',
	'MAIN',
	'NAV',
	'SECTION',
	'SUMMARY',
	'TABLE',
	'TBODY',
	'TD',
	'TFOOT',
	'TH',
	'THEAD',
	'TR',
]);

const BLOCK_SELECTOR = [...EDITOR_BLOCK_TAGS, ...CONTAINER_TAGS].join(',');

const isElement = (node: Node): node is Element => node.nodeType === Node.ELEMENT_NODE;

const isEditorBlock = (node: Node): boolean => isElement(node) && EDITOR_BLOCK_TAGS.has(node.tagName);

// A container by tag, or an inline element such as a `<span>` wrapped around blocks: its
// blocks must not end up inside the paragraph that wraps its neighbours.
const isContainer = (node: Node): boolean =>
	isElement(node) && (CONTAINER_TAGS.has(node.tagName) || node.querySelector(BLOCK_SELECTOR) !== null);

// Nothing the editor would show. Comments come from copied html (`<!--StartFragment-->`).
const isBlank = (node: Node): boolean =>
	node.nodeType === Node.COMMENT_NODE || (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim());

/**
 * Wraps each run of inline content that is not inside a block in a `<p>`, and returns the html
 * unchanged when there is none.
 *
 * The editor only builds a paragraph from a `<p>`. Inline content outside one — bare text, or
 * text directly inside a `<div>`, which is how the data importer stores descriptions, or in a
 * table cell — loads as loose text at the editor's root. That is invalid for the editor, and
 * the first keystroke makes it normalise the root, which discards all of that text: the
 * user's typing replaces the whole description. Wrapping the runs up front gives the editor
 * the paragraphs it needs.
 *
 * Only for content the DraftJS converter could not render. It already emits every block it
 * writes, and some of its shapes (an aligned image's `<div style="text-align">`) must reach
 * the editor exactly as written.
 */
export const wrapLooseInlineContent = (html: string): string => {
	if (!html.trim()) {
		return html;
	}

	const body = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body;
	let wrapped = false;

	const wrapRuns = (container: Element) => {
		let run: Node[] = [];
		const flush = () => {
			if (run.some(node => !isBlank(node))) {
				const paragraph = container.ownerDocument.createElement('p');
				container.insertBefore(paragraph, run[0]);
				run.forEach(node => paragraph.appendChild(node));
				wrapped = true;
			}
			run = [];
		};

		Array.from(container.childNodes).forEach(child => {
			if (isEditorBlock(child)) {
				flush();
			} else if (isContainer(child)) {
				flush();
				wrapRuns(child as Element);
			} else {
				run.push(child);
			}
		});
		flush();
	};

	wrapRuns(body);
	return wrapped ? body.innerHTML : html;
};
